import {
  Room,
  RoomEvent,
  Track,
  createLocalAudioTrack,
  type LocalAudioTrack,
  type RemoteTrack,
} from "livekit-client";
import { DuplexClient, type DuplexSession } from "@repo/duplex-client";

type WidgetPosition = "bottom-right" | "bottom-left" | "top-right" | "top-left";
type WidgetLauncherSize = "compact" | "comfortable" | "large";

type WidgetTheme = {
  primaryColor: string;
  accentColor: string;
  surfaceColor: string;
  textColor: string;
  mutedTextColor: string;
  buttonTextColor: string;
  borderColor: string;
  position: WidgetPosition;
  launcherSize: WidgetLauncherSize;
  panelWidth: number;
  borderRadius: number;
  defaultOpen: boolean;
  showAvatar: boolean;
  avatarImageUrl: string | null;
  avatarOrbColor1: string;
  avatarOrbColor2: string;
  brandName: string;
  actionText: string;
  welcomeText: string;
  startButtonText: string;
  endButtonText: string;
  connectingText: string;
  listeningText: string;
  speakingText: string;
  thinkingText: string;
  endedText: string;
  whiteLabel: boolean;
};

type PublicWidgetConfig = {
  transport?: "duplex";
  widgetId: string;
  name: string;
  agentName: string;
  theme: WidgetTheme;
  consentRequired: boolean;
  consentText: string;
  sessionTtlSeconds: number;
};

type LiveKitWidgetSession = {
  transport?: "livekit";
  sessionId: string;
  endToken: string;
  livekitUrl: string;
  roomName: string;
  participant: {
    identity: string;
    name: string;
    token: string;
    ttlSeconds: number;
  };
  expiresAt: string;
};

type PublicWidgetSession = LiveKitWidgetSession | {
  transport: "duplex";
  sessionId: string;
  endToken: string;
  roomName: string;
  expiresAt: string;
  duplex: DuplexSession;
};

type ApiEnvelope<T> = { success: boolean; data: T; message?: string };
type WidgetState =
  | "loading"
  | "idle"
  | "requesting"
  | "connecting"
  | "live"
  | "ended"
  | "error";

const loaderScript = document.currentScript as HTMLScriptElement | null;

const DEFAULT_THEME: WidgetTheme = {
  primaryColor: "#4de2e6",
  accentColor: "#ff9fd3",
  surfaceColor: "#090909",
  textColor: "#f5f5f5",
  mutedTextColor: "#b3b3b3",
  buttonTextColor: "#f5f5f5",
  borderColor: "#353535",
  position: "bottom-right",
  launcherSize: "comfortable",
  panelWidth: 340,
  borderRadius: 24,
  defaultOpen: false,
  showAvatar: true,
  avatarImageUrl: null,
  avatarOrbColor1: "#4de2e6",
  avatarOrbColor2: "#ff9fd3",
  brandName: "zehn/call-center",
  actionText: "Talk to us",
  welcomeText: "Talk with our voice agent.",
  startButtonText: "Start call",
  endButtonText: "End call",
  connectingText: "Connecting",
  listeningText: "Listening",
  speakingText: "Assistant speaking",
  thinkingText: "Thinking",
  endedText: "Call ended",
  whiteLabel: false,
};

class ZehnCallCenterWidgetElement extends HTMLElement {
  private readonly root: ShadowRoot;
  private config: PublicWidgetConfig | null = null;
  private session: PublicWidgetSession | null = null;
  private room: Room | null = null;
  private localTrack: LocalAudioTrack | null = null;
  private state: WidgetState = "loading";
  private expanded = false;
  private consentAccepted = false;
  private errorMessage = "";
  private audioContainer: HTMLDivElement | null = null;
  private duplexClient: DuplexClient | null = null;
  private speaking = false;
  private thinking = false;
  private notice = "";
  private noticeTimer: ReturnType<typeof setTimeout> | null = null;
  private attempt = 0;

  constructor() {
    super();
    this.root = this.attachShadow({ mode: "open" });
  }

  connectedCallback() {
    this.expanded = this.getAttribute("open") === "true";
    this.render();
    void this.loadConfig();
  }

  disconnectedCallback() {
    void this.endCall({ notifyServer: true });
  }

  private get widgetId() {
    return this.getAttribute("widget-id") || this.getAttribute("data-widget-id") || "";
  }

  private get apiBaseUrl() {
    const explicit = this.getAttribute("api-base-url");
    if (explicit) return explicit.replace(/\/+$/, "");
    const scriptSrc = loaderScript?.src;
    if (scriptSrc) {
      try {
        return `${new URL(scriptSrc).origin}/api/v1`;
      } catch {
        // Fall through to current origin.
      }
    }
    return `${window.location.origin}/api/v1`;
  }

  private async loadConfig() {
    const widgetId = this.widgetId;
    if (!widgetId) {
      this.setError("Widget id is missing.");
      return;
    }

    try {
      const config = await this.apiGet<PublicWidgetConfig>(
        `/public/widgets/${encodeURIComponent(widgetId)}/config`,
      );
      this.config = {
        ...config,
        theme: normalizeTheme(config.theme),
      };
      if (!this.hasAttribute("open")) {
        this.expanded = this.currentTheme().defaultOpen;
      }
      this.state = "idle";
      this.render();
    } catch (error) {
      this.setError(errorMessage(error, "Widget is unavailable."));
    }
  }

  private async startCall() {
    if (
      !this.config ||
      this.state === "requesting" ||
      this.state === "connecting" ||
      this.state === "live"
    ) {
      return;
    }
    if (this.config.consentRequired && !this.consentAccepted) {
      this.setError("Accept the call notice before starting.");
      return;
    }

    this.errorMessage = "";
    const attempt = ++this.attempt;
    this.state = "requesting";
    this.render();

    try {
      if (!window.isSecureContext || !navigator.mediaDevices?.getUserMedia) {
        throw new Error(
          "Microphone access needs a secure connection. Open this page over https or on localhost."
        );
      }
      if (this.config.transport === "duplex") {
        const client = new DuplexClient({
          onClose: () => { void this.endCall({ notifyServer: true }); },
          onError: (error) => {
            void this.endCall({ notifyServer: true });
            this.setError(error.message);
          },
          onPlayback: (playing) => {
            if (playing) this.thinking = false;
            if (this.speaking === playing) return;
            this.speaking = playing;
            if (this.state === "live") this.render();
          },
          onEvent: (event) => {
            if (event.type === "transcript") this.dispatchEvent(new CustomEvent("zehn:transcript", { detail: event }));
            if (event.type === "turn_started") this.setThinking(true);
            if (event.type === "turn_finished" || event.type === "interrupted" || event.type === "rejected" || event.type === "error") {
              this.setThinking(false);
            }
            if (event.type === "error") this.showNotice(event.message || "Voice provider request failed. Please try again.");
          },
        });
        this.duplexClient = client;
        await client.prepare();
      } else {
        const localTrack = await createLocalAudioTrack({
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        });
        if (attempt !== this.attempt) { localTrack.stop(); return; }
        this.localTrack = localTrack;
      }
      if (attempt !== this.attempt) return;
      this.state = "connecting";
      this.render();

      const session = await this.apiPost<PublicWidgetSession>(
        `/public/widgets/${encodeURIComponent(this.widgetId)}/sessions`,
        {
          visitorId: this.visitorId(),
        },
      );
      if (attempt !== this.attempt) {
        await this.apiPost(`/public/widgets/${encodeURIComponent(this.widgetId)}/sessions/${encodeURIComponent(session.sessionId)}/end`, { endToken: session.endToken });
        return;
      }
      this.session = session;

      if (session.transport === "duplex") {
        if (!this.duplexClient) throw new Error("Voice runtime changed. Please reconnect.");
        await this.duplexClient.connect(session.duplex);
        if (attempt !== this.attempt) return;
        this.state = "live";
        this.render();
        return;
      }
      if (!this.localTrack) throw new Error("Voice runtime changed. Please reconnect.");

      const room = new Room({
        adaptiveStream: true,
        dynacast: true,
      });
      this.room = room;
      room
        .on(RoomEvent.TrackSubscribed, (track) => this.attachRemoteTrack(track))
        .on(RoomEvent.TrackUnsubscribed, (track) => {
          track.detach().forEach((element) => element.remove());
        })
        .on(RoomEvent.Disconnected, () => {
          this.cleanupAudio();
          if (this.state === "live" || this.state === "connecting") {
            this.state = "ended";
            this.render();
          }
        })
        .on(RoomEvent.Reconnecting, () => {
          this.state = "connecting";
          this.render();
        })
        .on(RoomEvent.Reconnected, () => {
          this.state = "live";
          this.render();
        });

      await room.connect(session.livekitUrl, session.participant.token);
      await room.localParticipant.publishTrack(this.localTrack);
      this.state = "live";
      this.render();
    } catch (error) {
      if (attempt !== this.attempt) return;
      await this.endCall({ notifyServer: true });
      this.setError(errorMessage(error, "Could not start the call."));
    }
  }

  private async endCall(options: { notifyServer: boolean }) {
    const attempt = ++this.attempt;
    const session = this.session;
    this.session = null;
    this.duplexClient?.close();
    this.duplexClient = null;
    this.speaking = false;
    this.thinking = false;
    this.clearNotice();
    this.localTrack?.stop();
    this.localTrack = null;
    this.room?.disconnect();
    this.room = null;
    this.cleanupAudio();

    if (options.notifyServer && session) {
      try {
        await this.apiPost(
          `/public/widgets/${encodeURIComponent(this.widgetId)}/sessions/${encodeURIComponent(session.sessionId)}/end`,
          { endToken: session.endToken },
        );
      } catch {
        // The local call is already disconnected. Server cleanup is best-effort.
      }
    }

    if (attempt !== this.attempt) return;
    if (this.state !== "error") {
      this.state = "ended";
      this.render();
    }
  }

  private attachRemoteTrack(track: RemoteTrack) {
    if (track.kind !== Track.Kind.Audio) return;
    this.audioContainer =
      this.audioContainer ??
      this.root.querySelector<HTMLDivElement>('[data-audio-container="true"]');
    if (!this.audioContainer) return;
    this.audioContainer.appendChild(track.attach());
  }

  private cleanupAudio() {
    this.audioContainer?.querySelectorAll("audio").forEach((element) => element.remove());
  }

  private visitorId() {
    const key = "zehn_call_center_widget_visitor_id";
    try {
      const existing = window.localStorage.getItem(key);
      if (existing) return existing;
      const next =
        window.crypto?.randomUUID?.() ??
        `visitor_${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
      window.localStorage.setItem(key, next);
      return next;
    } catch {
      return undefined;
    }
  }

  private async apiGet<T>(path: string): Promise<T> {
    const response = await fetch(`${this.apiBaseUrl}${path}`, {
      method: "GET",
      credentials: "omit",
      headers: { Accept: "application/json" },
    });
    return unwrapResponse<T>(response);
  }

  private async apiPost<T = unknown>(path: string, body: unknown): Promise<T> {
    const response = await fetch(`${this.apiBaseUrl}${path}`, {
      method: "POST",
      credentials: "omit",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body),
    });
    return unwrapResponse<T>(response);
  }

  private setThinking(thinking: boolean) {
    if (this.thinking === thinking) return;
    this.thinking = thinking;
    if (this.state === "live" && !this.speaking) this.render();
  }

  private showNotice(message: string) {
    this.notice = message;
    if (this.noticeTimer) clearTimeout(this.noticeTimer);
    this.noticeTimer = setTimeout(() => {
      this.noticeTimer = null;
      this.notice = "";
      if (this.state === "live") this.render();
    }, 6000);
    if (this.state === "live") this.render();
  }

  private clearNotice() {
    if (this.noticeTimer) clearTimeout(this.noticeTimer);
    this.noticeTimer = null;
    this.notice = "";
  }

  private setError(message: string) {
    this.errorMessage = message;
    this.state = "error";
    this.expanded = true;
    this.render();
  }

  private currentTheme() {
    return themeWithAttributeOverrides(
      normalizeTheme(this.config?.theme),
      this,
    );
  }

  private statusText(theme: WidgetTheme) {
    if (this.state === "loading") return "Loading";
    if (this.state === "requesting") return "Requesting microphone";
    if (this.state === "connecting") return theme.connectingText;
    if (this.state === "live") {
      if (this.notice) return this.notice;
      if (this.speaking) return theme.speakingText;
      return this.thinking ? theme.thinkingText : theme.listeningText;
    }
    if (this.state === "ended") return theme.endedText;
    if (this.state === "error") return "Unable to connect";
    return "Ready";
  }

  private render() {
    const theme = this.currentTheme();
    const position = positionStyles(theme.position);
    const launcher = launcherSize(theme.launcherSize);
    const brandName = escapeHtml(theme.brandName);
    const welcomeText = escapeHtml(theme.welcomeText);
    const startButtonText = escapeHtml(theme.startButtonText);
    const endButtonText = escapeHtml(theme.endButtonText);
    const consentText = escapeHtml(this.config?.consentText ?? "");
    const statusText = escapeHtml(this.statusText(theme));
    const primaryColor = safeColor(theme.primaryColor, DEFAULT_THEME.primaryColor);
    const accentColor = safeColor(theme.accentColor, DEFAULT_THEME.accentColor);
    const surfaceColor = safeColor(theme.surfaceColor, DEFAULT_THEME.surfaceColor);
    const textColor = safeColor(theme.textColor, DEFAULT_THEME.textColor);
    const mutedTextColor = safeColor(theme.mutedTextColor, DEFAULT_THEME.mutedTextColor);
    const buttonTextColor = safeColor(theme.buttonTextColor, DEFAULT_THEME.buttonTextColor);
    const borderColor = safeColor(theme.borderColor, DEFAULT_THEME.borderColor);
    const avatarOrbColor1 = safeColor(theme.avatarOrbColor1, DEFAULT_THEME.avatarOrbColor1);
    const avatarOrbColor2 = safeColor(theme.avatarOrbColor2, DEFAULT_THEME.avatarOrbColor2);
    const branded = primaryColor.toLowerCase() === DEFAULT_THEME.primaryColor &&
      accentColor.toLowerCase() === DEFAULT_THEME.accentColor;
    const warmthColor = branded ? "#f9ff7c" : accentColor;
    const defaultAction = branded && surfaceColor.toLowerCase() === DEFAULT_THEME.surfaceColor &&
      buttonTextColor.toLowerCase() === DEFAULT_THEME.buttonTextColor;
    const panelWidth = clampNumber(theme.panelWidth, 280, 420);
    const borderRadius = clampNumber(theme.borderRadius, 0, 32);
    const controlRadius = Math.max(borderRadius - 10, 0);
    const isBusy = this.state === "loading" || this.state === "requesting" || this.state === "connecting";
    const isLive = this.state === "live";
    const canStart =
      this.state === "idle" ||
      this.state === "ended" ||
      this.state === "error";
    const consentRequired = Boolean(this.config?.consentRequired);
    const startDisabled = isBusy || (consentRequired && !this.consentAccepted);
    const bubbleLabel = escapeHtml(isLive ? theme.listeningText : theme.actionText);
    const avatarMarkup = theme.showAvatar
      ? avatarHtml({ theme, avatarOrbColor1, avatarOrbColor2 })
      : "";

    this.root.innerHTML = `
      <style>
        :host {
          all: initial;
          font-family: Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
          --qv-primary: ${primaryColor};
          --qv-accent: ${accentColor};
          --qv-warmth: ${warmthColor};
          --qv-edge: linear-gradient(125deg, color-mix(in srgb, var(--qv-accent) 78%, transparent), color-mix(in srgb, var(--qv-warmth) 55%, transparent) 24%, color-mix(in srgb, var(--qv-text) 10%, transparent) 46%, color-mix(in srgb, var(--qv-primary) 55%, transparent) 72%, color-mix(in srgb, var(--qv-accent) 72%, transparent));
          --qv-border: ${borderColor};
          --qv-muted: ${mutedTextColor};
          --qv-text: ${textColor};
          --qv-button-text: ${buttonTextColor};
          --qv-surface: ${surfaceColor};
          --qv-radius: ${borderRadius}px;
          --qv-control-radius: ${controlRadius}px;
        }
        *, *::before, *::after { box-sizing: border-box; }
        .qv-shell {
          position: fixed;
          z-index: 2147483000;
          ${position.vertical}: 24px;
          ${position.horizontal}: 24px;
          display: flex;
          width: min(${panelWidth}px, calc(100vw - 32px));
          flex-direction: ${position.isTop ? "column-reverse" : "column"};
          align-items: ${position.horizontal === "right" ? "flex-end" : "flex-start"};
          gap: 14px;
          font-family: inherit;
          line-height: 1.5;
          color: var(--qv-text);
          -webkit-font-smoothing: antialiased;
        }
        .qv-panel {
          display: ${this.expanded ? "block" : "none"};
          position: relative;
          width: 100%;
          max-height: calc(100dvh - ${launcher.height + 76}px);
          overflow: auto;
          border: 1px solid color-mix(in srgb, var(--qv-border) 45%, transparent);
          border-radius: var(--qv-radius);
          background: radial-gradient(ellipse at 100% 0%, color-mix(in srgb, var(--qv-accent) 4%, transparent), transparent 42%), radial-gradient(ellipse at 0% 100%, color-mix(in srgb, var(--qv-warmth) 3%, transparent), transparent 42%), linear-gradient(145deg, color-mix(in srgb, var(--qv-text) 5%, transparent), transparent 48%), color-mix(in srgb, var(--qv-surface) 80%, transparent);
          -webkit-backdrop-filter: blur(28px) saturate(155%);
          backdrop-filter: blur(28px) saturate(155%);
          box-shadow: 0 24px 70px rgba(0, 0, 0, 0.3), inset 0 1px 0 color-mix(in srgb, var(--qv-text) 15%, transparent), 0 0 36px color-mix(in srgb, var(--qv-accent) 2%, transparent);
        }
        .qv-panel::after, .qv-bubble::before, .qv-primary::before {
          content: "";
          position: absolute;
          inset: 0;
          border-radius: inherit;
          padding: 1px;
          background: var(--qv-edge);
          -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
          -webkit-mask-composite: xor;
          mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
          mask-composite: exclude;
          pointer-events: none;
          z-index: 1;
        }
        .qv-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          border-bottom: 1px solid color-mix(in srgb, var(--qv-border) 70%, transparent);
          padding: 18px;
          background: linear-gradient(135deg, color-mix(in srgb, var(--qv-text) 4%, transparent), transparent);
        }
        .qv-brand {
          display: flex;
          min-width: 0;
          align-items: center;
          gap: 12px;
        }
        .qv-brand-copy { min-width: 0; }
        .qv-avatar, .qv-orb {
          width: 38px;
          height: 38px;
          flex: none;
          border-radius: 50%;
        }
        .qv-avatar { object-fit: cover; }
        .qv-orb {
          background: radial-gradient(circle at 30% 25%, color-mix(in srgb, ${avatarOrbColor1} 30%, white), transparent 55%), linear-gradient(140deg, ${avatarOrbColor1}, ${avatarOrbColor2});
          box-shadow: inset -4px -5px 10px rgba(0, 0, 0, 0.22), 0 0 20px color-mix(in srgb, var(--qv-primary) 14%, transparent);
        }
        .qv-title {
          overflow: hidden;
          margin: 0;
          text-overflow: ellipsis;
          white-space: nowrap;
          font-size: 14px;
          font-weight: 650;
          letter-spacing: -0.025em;
        }
        .qv-status {
          margin: 3px 0 0;
          color: var(--qv-muted);
          font-size: 11px;
          overflow-wrap: anywhere;
        }
        .qv-body { padding: 24px 20px 18px; }
        .qv-visual {
          position: relative;
          display: grid;
          place-items: center;
          width: 108px;
          height: 108px;
          margin: 0 auto 22px;
          border: 1px solid color-mix(in srgb, var(--qv-primary) 18%, transparent);
          border-radius: 50%;
          background: radial-gradient(ellipse at 72% 20%, color-mix(in srgb, var(--qv-accent) 6%, transparent), transparent 60%), radial-gradient(ellipse at 10% 90%, color-mix(in srgb, var(--qv-warmth) 5%, transparent), transparent 65%);
          box-shadow: inset 0 1px 0 color-mix(in srgb, var(--qv-text) 14%, transparent), 0 0 32px color-mix(in srgb, var(--qv-accent) 3%, transparent);
        }
        .qv-visual::before {
          content: "";
          position: absolute;
          inset: 10px;
          border: 1px solid color-mix(in srgb, var(--qv-accent) 22%, transparent);
          border-radius: inherit;
        }
        .qv-wave {
          display: flex;
          height: 38px;
          align-items: center;
          gap: 5px;
          color: var(--qv-primary);
        }
        .qv-wave span {
          display: block;
          width: 4px;
          height: 10px;
          border-radius: 5px;
          background: currentColor;
        }
        .qv-wave span:nth-child(2), .qv-wave span:nth-child(4) { height: 23px; }
        .qv-wave span:nth-child(3) { height: 36px; color: var(--qv-accent); }
        .qv-visual[data-active="true"] .qv-wave span { animation: qv-wave 1.2s ease-in-out infinite alternate; }
        .qv-wave span:nth-child(2) { animation-delay: -0.4s !important; }
        .qv-wave span:nth-child(3) { animation-delay: -0.8s !important; }
        .qv-wave span:nth-child(4) { animation-delay: -0.2s !important; }
        .qv-wave span:nth-child(5) { animation-delay: -0.6s !important; }
        .qv-welcome {
          margin: 0 0 22px;
          text-align: center;
          font-size: 15px;
          line-height: 1.65;
          letter-spacing: -0.02em;
          overflow-wrap: anywhere;
        }
        .qv-consent {
          display: flex;
          align-items: flex-start;
          gap: 10px;
          margin-bottom: 18px;
          color: var(--qv-muted);
          font-size: 12px;
          line-height: 1.6;
          cursor: pointer;
        }
        .qv-consent input { margin: 3px 0 0; flex: none; accent-color: var(--qv-primary); }
        .qv-consent input:focus-visible { outline: 2px solid var(--qv-primary); outline-offset: 3px; }
        .qv-actions { display: grid; gap: 8px; }
        button {
          position: relative;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 9px;
          appearance: none;
          border: 1px solid var(--qv-border);
          border-radius: var(--qv-control-radius);
          cursor: pointer;
          font-family: inherit;
          font-size: 13px;
          font-weight: 650;
          transition: transform 160ms ease, box-shadow 160ms ease, opacity 160ms ease;
        }
        button:hover:not(:disabled) { transform: translateY(-1px); }
        button:focus-visible { outline: 2px solid var(--qv-primary); outline-offset: 4px; }
        button:disabled { cursor: not-allowed; opacity: 0.55; }
        button svg { width: 18px; height: 18px; flex: none; }
        .qv-primary {
          min-height: 48px;
          padding: 10px 16px;
          border-color: transparent;
          background: ${defaultAction ? "linear-gradient(135deg, color-mix(in srgb, var(--qv-text) 5%, transparent), color-mix(in srgb, var(--qv-surface) 58%, transparent))" : "linear-gradient(120deg, var(--qv-primary), color-mix(in srgb, var(--qv-primary) 82%, var(--qv-accent)))"};
          color: var(--qv-button-text);
          -webkit-backdrop-filter: blur(20px) saturate(145%);
          backdrop-filter: blur(20px) saturate(145%);
          box-shadow: inset 0 1px 0 color-mix(in srgb, var(--qv-text) 12%, transparent), 0 8px 20px rgba(0, 0, 0, 0.1);
        }
        .qv-close {
          width: 34px;
          height: 34px;
          flex: none;
          padding: 0;
          border-color: transparent;
          border-radius: 10px;
          background: color-mix(in srgb, var(--qv-text) 5%, transparent);
          color: var(--qv-muted);
        }
        .qv-bubble {
          min-height: ${launcher.height}px;
          max-width: 100%;
          padding: 0 ${launcher.padding}px;
          border-color: transparent;
          border-radius: var(--qv-radius);
          background: radial-gradient(ellipse at 100% 0%, color-mix(in srgb, var(--qv-accent) 4%, transparent), transparent 48%), linear-gradient(135deg, color-mix(in srgb, var(--qv-text) 6%, transparent), transparent), color-mix(in srgb, var(--qv-surface) 80%, transparent);
          -webkit-backdrop-filter: blur(24px) saturate(145%);
          backdrop-filter: blur(24px) saturate(145%);
          color: var(--qv-text);
          box-shadow: 0 10px 32px rgba(0, 0, 0, 0.24), inset 0 1px 0 color-mix(in srgb, var(--qv-text) 16%, transparent);
          font-size: ${launcher.fontSize}px;
        }
        .qv-bubble svg { color: var(--qv-text); }
        .qv-dot {
          width: 6px;
          height: 6px;
          flex: none;
          border-radius: 50%;
          background: var(--qv-warmth);
          box-shadow: 0 0 10px color-mix(in srgb, var(--qv-warmth) 35%, transparent);
          opacity: ${isLive ? "1" : "0.65"};
        }
        .qv-error {
          margin: 0 0 16px;
          border: 1px solid color-mix(in srgb, var(--qv-text) 12%, transparent);
          border-radius: 10px;
          padding: 10px 12px;
          background: color-mix(in srgb, #dc2626 8%, transparent);
          color: var(--qv-text);
          font-size: 12px;
          overflow-wrap: anywhere;
        }
        .qv-powered { margin: 16px 0 0; color: var(--qv-muted); font-size: 10px; text-align: center; letter-spacing: 0.025em; }
        @keyframes qv-wave { to { transform: scaleY(0.45); opacity: 0.6; } }
        @media (max-width: 480px) {
          .qv-shell { ${position.horizontal}: 16px; ${position.vertical}: 16px; }
        }
        @media (prefers-reduced-motion: reduce) {
          *, *::before, *::after { animation: none !important; transition: none !important; }
          button:hover:not(:disabled) { transform: none; }
        }
      </style>
      <div class="qv-shell">
        <section class="qv-panel" id="qv-panel" aria-label="${brandName}" aria-live="polite">
          <div class="qv-header">
            <div class="qv-brand">
              ${avatarMarkup}
              <div class="qv-brand-copy">
                <p class="qv-title">${brandName}</p>
                <p class="qv-status">${statusText}</p>
              </div>
            </div>
            <button class="qv-close" type="button" data-action="collapse" aria-label="Close widget"><svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="m6 6 12 12M18 6 6 18" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg></button>
          </div>
          <div class="qv-body">
            <div class="qv-visual" data-active="${isLive && this.speaking}" aria-hidden="true"><div class="qv-wave"><span></span><span></span><span></span><span></span><span></span></div></div>
            <p class="qv-welcome">${welcomeText}</p>
            ${this.state === "error" ? `<p class="qv-error">${escapeHtml(this.errorMessage)}</p>` : ""}
            ${
              consentRequired && !isLive
                ? `<label class="qv-consent">
                    <input type="checkbox" data-action="consent" ${this.consentAccepted ? "checked" : ""} />
                    <span>${consentText}</span>
                  </label>`
                : ""
            }
            <div class="qv-actions">
              ${
                isLive
                  ? `<button class="qv-primary" type="button" data-action="end">${endButtonText}</button>`
                  : `<button class="qv-primary" type="button" data-action="start" ${!canStart || startDisabled ? "disabled" : ""}>${isBusy ? statusText : startButtonText}</button>`
              }
            </div>
            ${theme.whiteLabel ? "" : `<p class="qv-powered">Powered by zehn/call-center</p>`}
          </div>
        </section>
        <button class="qv-bubble" type="button" data-action="toggle" aria-controls="qv-panel" aria-expanded="${this.expanded ? "true" : "false"}">
          <svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M5 10v4m4-7v10m3-13v16m4-13v10m4-7v4" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>
          <span>${bubbleLabel}</span>
          <span class="qv-dot" aria-hidden="true"></span>
        </button>
      </div>
      <div data-audio-container="true" hidden></div>
    `;
    this.audioContainer = this.root.querySelector<HTMLDivElement>(
      '[data-audio-container="true"]',
    );

    this.root.querySelector('[data-action="toggle"]')?.addEventListener("click", () => {
      this.expanded = !this.expanded;
      this.render();
    });
    this.root.querySelector('[data-action="collapse"]')?.addEventListener("click", () => {
      this.expanded = false;
      this.render();
    });
    this.root.querySelector('[data-action="start"]')?.addEventListener("click", () => {
      void this.startCall();
    });
    this.root.querySelector('[data-action="end"]')?.addEventListener("click", () => {
      void this.endCall({ notifyServer: true });
    });
    this.root.querySelector('[data-action="consent"]')?.addEventListener("change", (event) => {
      this.consentAccepted = (event.target as HTMLInputElement).checked;
      this.errorMessage = "";
      if (this.state === "error") this.state = "idle";
      this.render();
    });
  }
}

async function unwrapResponse<T>(response: Response): Promise<T> {
  let body: ApiEnvelope<T> | { message?: string } | null = null;
  try {
    body = (await response.json()) as ApiEnvelope<T>;
  } catch {
    body = null;
  }
  if (!response.ok) {
    throw new Error(body?.message || `Request failed with ${response.status}`);
  }
  if (body && "data" in body) return body.data;
  throw new Error("Malformed widget response.");
}

function normalizeTheme(value?: Partial<WidgetTheme> | null): WidgetTheme {
  return { ...DEFAULT_THEME, ...(value ?? {}) };
}

function themeWithAttributeOverrides(
  value: WidgetTheme,
  element: HTMLElement,
): WidgetTheme {
  const theme = { ...value };
  setColorOverride(element, theme, "primary-color", "primaryColor");
  setColorOverride(element, theme, "accent-color", "accentColor");
  setColorOverride(element, theme, "surface-color", "surfaceColor");
  setColorOverride(element, theme, "text-color", "textColor");
  setColorOverride(element, theme, "muted-text-color", "mutedTextColor");
  setColorOverride(element, theme, "button-text-color", "buttonTextColor");
  setColorOverride(element, theme, "border-color", "borderColor");
  setColorOverride(element, theme, "avatar-orb-color-1", "avatarOrbColor1");
  setColorOverride(element, theme, "avatar-orb-color-2", "avatarOrbColor2");

  const position = element.getAttribute("position");
  if (isWidgetPosition(position)) theme.position = position;
  const launcherSizeValue = element.getAttribute("launcher-size");
  if (isLauncherSize(launcherSizeValue)) theme.launcherSize = launcherSizeValue;

  theme.panelWidth = numberAttribute(element, "panel-width", theme.panelWidth, 280, 420);
  theme.borderRadius = numberAttribute(element, "border-radius", theme.borderRadius, 0, 32);
  theme.defaultOpen = booleanAttribute(element, "default-open", theme.defaultOpen);
  theme.showAvatar = booleanAttribute(element, "show-avatar", theme.showAvatar);

  setTextOverride(element, theme, "brand-name", "brandName", 80);
  setTextOverride(element, theme, "action-text", "actionText", 60);
  setTextOverride(element, theme, "welcome-text", "welcomeText", 160);
  setTextOverride(element, theme, "start-call-text", "startButtonText", 40);
  setTextOverride(element, theme, "end-call-text", "endButtonText", 40);
  setTextOverride(element, theme, "connecting-text", "connectingText", 40);
  setTextOverride(element, theme, "listening-text", "listeningText", 40);
  setTextOverride(element, theme, "speaking-text", "speakingText", 40);
  setTextOverride(element, theme, "thinking-text", "thinkingText", 40);
  setTextOverride(element, theme, "ended-text", "endedText", 40);

  const avatarImageUrl = safeImageUrl(element.getAttribute("avatar-image-url"));
  if (avatarImageUrl) theme.avatarImageUrl = avatarImageUrl;

  return theme;
}

function avatarHtml(input: {
  theme: WidgetTheme;
  avatarOrbColor1: string;
  avatarOrbColor2: string;
}) {
  const imageUrl = safeImageUrl(input.theme.avatarImageUrl);
  if (imageUrl) {
    return `<img class="qv-avatar" src="${escapeAttribute(imageUrl)}" alt="" />`;
  }
  return `<span class="qv-orb" aria-hidden="true"></span>`;
}

function setColorOverride<K extends ColorThemeKey>(
  element: HTMLElement,
  theme: WidgetTheme,
  attr: string,
  key: K,
) {
  const value = element.getAttribute(attr);
  if (value && isHexColor(value)) {
    (theme as Record<ColorThemeKey, string>)[key] = value;
  }
}

type ColorThemeKey =
  | "primaryColor"
  | "accentColor"
  | "surfaceColor"
  | "textColor"
  | "mutedTextColor"
  | "buttonTextColor"
  | "borderColor"
  | "avatarOrbColor1"
  | "avatarOrbColor2";

function setTextOverride<K extends StringThemeKey>(
  element: HTMLElement,
  theme: WidgetTheme,
  attr: string,
  key: K,
  maxLength: number,
) {
  const value = element.getAttribute(attr)?.trim();
  if (value) theme[key] = value.slice(0, maxLength) as WidgetTheme[K];
}

type StringThemeKey =
  | "brandName"
  | "actionText"
  | "welcomeText"
  | "startButtonText"
  | "endButtonText"
  | "connectingText"
  | "listeningText"
  | "speakingText"
  | "thinkingText"
  | "endedText";

function numberAttribute(
  element: HTMLElement,
  attr: string,
  fallback: number,
  min: number,
  max: number,
) {
  if (!element.hasAttribute(attr)) return fallback;
  const value = Number(element.getAttribute(attr));
  return Number.isFinite(value) ? clampNumber(value, min, max) : fallback;
}

function booleanAttribute(element: HTMLElement, attr: string, fallback: boolean) {
  if (!element.hasAttribute(attr)) return fallback;
  const value = element.getAttribute(attr);
  return value !== "false" && value !== "0";
}

function positionStyles(position: WidgetPosition) {
  return {
    vertical: position.startsWith("top") ? "top" : "bottom",
    horizontal: position.endsWith("left") ? "left" : "right",
    isTop: position.startsWith("top"),
  };
}

function launcherSize(size: WidgetLauncherSize) {
  if (size === "compact") return { height: 48, padding: 16, fontSize: 13 };
  if (size === "large") return { height: 64, padding: 22, fontSize: 15 };
  return { height: 56, padding: 18, fontSize: 14 };
}

function escapeHtml(value: string) {
  return value.replace(/[&<>"']/g, (char) => {
    switch (char) {
      case "&":
        return "&amp;";
      case "<":
        return "&lt;";
      case ">":
        return "&gt;";
      case '"':
        return "&quot;";
      default:
        return "&#39;";
    }
  });
}

function escapeAttribute(value: string) {
  return escapeHtml(value);
}

function safeColor(value: string, fallback: string) {
  return isHexColor(value) ? value : fallback;
}

function isHexColor(value: string) {
  return /^#[0-9A-Fa-f]{6}$/.test(value);
}

function isWidgetPosition(value: string | null): value is WidgetPosition {
  return (
    value === "bottom-right" ||
    value === "bottom-left" ||
    value === "top-right" ||
    value === "top-left"
  );
}

function isLauncherSize(value: string | null): value is WidgetLauncherSize {
  return value === "compact" || value === "comfortable" || value === "large";
}

function safeImageUrl(value: string | null | undefined) {
  if (!value) return null;
  try {
    const url = new URL(value);
    return url.protocol === "https:" ? url.toString() : null;
  } catch {
    return null;
  }
}

function clampNumber(value: number, min: number, max: number) {
  if (!Number.isFinite(value)) return min;
  return Math.min(Math.max(Math.round(value), min), max);
}

function errorMessage(error: unknown, fallback: string) {
  return error instanceof Error && error.message ? error.message : fallback;
}

if (!customElements.get("zehn-call-center-widget")) {
  customElements.define("zehn-call-center-widget", ZehnCallCenterWidgetElement);
}
